import { useEffect } from 'react';
import { useAuth } from '@/hooks/auth.hooks';
import Card from '@/components/Card';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { twMerge } from 'tailwind-merge';
import { useParams } from 'react-router-dom';
import { TVehiculeCheckResultSchema } from '@/schemas/vehicule-check-result/vehicule-check-result.schema';
import { useGetAllRepair } from '@/hooks/vehicule-check-result.hooks';
import Loader from '@/components/Loading/Loader';
import { ResultPhotoList } from './ResultPhotoList';
import { TDamageSchema } from '@/schemas/damage/damage.schema';
import { TEquipmentSchema } from '@/schemas/equipment/equipment.schema';
import { TVehiculeSchema } from '@/schemas/vehicule/vehicule.schema';
import { ResultPhotoTab } from '@/type/ui/tab/vehiculeCheckResultTab';
import { useGetChildTab } from '@/hooks/tab';
import { TFunction } from 'i18next';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { useResultImageList } from '../hooks/result-image.hooks';
import { VehiculeCheckProgressIndicator } from '@/components/Custom/ProgressIndicator/VehiculeCheckProgressIndicator';
import { useVehiculeCheckVideoSockets } from '@/hooks/sockets/vehicule-check-video-sockets.hooks';

interface VehiculeCheckResultPhotosProps {
  t?: TFunction<undefined>;
  isLoading?: boolean;
  vehiculeCheckResult?: TVehiculeCheckResultSchema;
  isReadOnly?: boolean;
  moderationStatus: string;
}

export const VehiculeCheckResultPhotos = ({
  t,
  isLoading,
  vehiculeCheckResult,
  isReadOnly,
  moderationStatus,
}: VehiculeCheckResultPhotosProps) => {
  const { id } = useParams();

  const { loggedInUser } = useAuth();

  const { currentTab, navigateToTab } = useGetChildTab({
    defaultTab: ResultPhotoTab.EQUIPMENTS,
  });

  /**
   * === Redirect to Detailed Results if NOT logged in and NOT within allowed child tab ===
   */
  useEffect(() => {
    if (
      !loggedInUser &&
      currentTab !== ResultPhotoTab.EQUIPMENTS &&
      currentTab !== ResultPhotoTab.DAMAGES
    ) {
      navigateToTab(ResultPhotoTab.EQUIPMENTS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser, currentTab]);
  /**
   * === END OF Redirect to Detailed Results if NOT logged in and NOT within allowed child tab ===
   */

  const { data: repairData, isLoading: isLoadingRepairData } =
    useGetAllRepair();

  /**
   * === Hook for Vehicule Check Video Sockets ===
   */
  const {
    vehiculeCheckProgressStatus,
    videoUploadProgressPercent,
    isVideoProgressCompleted,
  } = useVehiculeCheckVideoSockets({
    vehiculeCheckId: id,
  });
  /**
   * === END OF Hook for Vehicule Check Video Sockets ===
   */

  const equipmentsCheckPointId = vehiculeCheckResult?.results?.find(
    (result) => result?.checkPoint?.equipments,
  )?.checkPoint?.id;

  const damagesCheckPointId = vehiculeCheckResult?.results?.find(
    (result) => result?.checkPoint?.damages,
  )?.checkPoint?.id;

  const carsCheckPointId = vehiculeCheckResult?.results?.find(
    (result) => result?.checkPoint?.vehicules,
  )?.checkPoint?.id;

  const equipmentList = useResultImageList(
    vehiculeCheckResult?.results,
    'equipments',
  );

  const damageList = useResultImageList(
    vehiculeCheckResult?.results,
    'damages',
  );

  const vehiculeList = useResultImageList(
    vehiculeCheckResult?.results,
    'vehicules',
  );

  return (
    <Card
      title={
        <TitleSection
          text={t('common:photos')}
          defaultIconClassName="ki-picture"
        />
      }
      bodyClassName="py-8 flex flex-col gap-5"
    >
      {!isVideoProgressCompleted ? (
        <VehiculeCheckProgressIndicator
          t={t}
          progress={videoUploadProgressPercent}
          vehiculeCheckProgressStatus={vehiculeCheckProgressStatus}
        />
      ) : (
        <>
          <TabList className="gap-3.5">
            <TabItem
              className={twMerge(
                '',
                currentTab !== ResultPhotoTab.EQUIPMENTS && 'border-black/50',
              )}
              text={t('common:equipmentPhotos')}
              leftIcon={<i className="ki-outline ki-abstract-41" />}
              active={currentTab === ResultPhotoTab.EQUIPMENTS}
              onChange={() => navigateToTab(ResultPhotoTab.EQUIPMENTS)}
            />
            <TabItem
              className={twMerge(
                '',
                currentTab !== ResultPhotoTab.DAMAGES && 'border-black/50',
              )}
              text={t('common:damagePhotos')}
              leftIcon={<i className="ki-outline ki-bucket-square" />}
              active={currentTab === ResultPhotoTab.DAMAGES}
              onChange={() => navigateToTab(ResultPhotoTab.DAMAGES)}
            />
            {!isReadOnly && (
              <TabItem
                className={twMerge(
                  '',
                  currentTab !== ResultPhotoTab.VEHICULES && 'border-black/50',
                )}
                text={t('common:vehiculePhotos')}
                leftIcon={<i className="ki-outline ki-parcel" />}
                active={currentTab === ResultPhotoTab.VEHICULES}
                onChange={() => navigateToTab(ResultPhotoTab.VEHICULES)}
              />
            )}
          </TabList>
          {currentTab === ResultPhotoTab.EQUIPMENTS &&
            (isLoading ? (
              <Loader />
            ) : (
              <ResultPhotoList<TEquipmentSchema>
                t={t}
                activeTab={currentTab}
                hidePhotoActionButtons
                checkPointId={equipmentsCheckPointId}
                vehiculeCheckResult={vehiculeCheckResult}
                hideAddImage
                data={equipmentList as TEquipmentSchema[]}
                title={t('common:equipments')}
                notFoundText={
                  equipmentsCheckPointId
                    ? `${t('equipmentPhotosNotFound')}...`
                    : `${t('equipmentCheckPointNotFound')}...`
                }
                moderationStatus={moderationStatus}
              />
            ))}
          {currentTab === ResultPhotoTab.DAMAGES &&
            (isLoading ? (
              <Loader />
            ) : (
              <ResultPhotoList<TDamageSchema>
                t={t}
                activeTab={currentTab}
                hidePhotoActionButtons
                checkPointId={damagesCheckPointId}
                vehiculeCheckResult={vehiculeCheckResult}
                hideAddImage
                data={damageList as TDamageSchema[]}
                isLoadingRepairData={isLoadingRepairData}
                repairData={repairData?.data}
                title={t('common:damages')}
                notFoundText={
                  damagesCheckPointId
                    ? `${t('damagePhotosNotFound')}...`
                    : `${t('damageCheckPointNotFound')}...`
                }
                moderationStatus={moderationStatus}
              />
            ))}
          {!isReadOnly &&
            currentTab === ResultPhotoTab.VEHICULES &&
            (isLoading ? (
              <Loader />
            ) : (
              <ResultPhotoList<TVehiculeSchema>
                t={t}
                activeTab={currentTab}
                hidePhotoActionButtons
                checkPointId={carsCheckPointId}
                vehiculeCheckResult={vehiculeCheckResult}
                hideAddImage
                data={vehiculeList as TVehiculeSchema[]}
                title={t('common:vehicules')}
                notFoundText={
                  carsCheckPointId
                    ? `${t('vehiculePhotosNotFound')}...`
                    : `${t('vehiculeCheckPointNotFound')}...`
                }
                moderationStatus={moderationStatus}
              />
            ))}
        </>
      )}
    </Card>
  );
};
